import React, { useState, useEffect } from 'react';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true); // Track loading state

  // Fetch notifications from API when the component loads
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/notifications`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
        });
  
        const data = await response.json();
  
        // Check if data is an array; otherwise, set an empty array
        if (Array.isArray(data)) {
          setNotifications(data);
        } else {
          setNotifications([]); // Ensures the UI correctly handles the response
        }
        
        setLoading(false); // Stop loading state
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setNotifications([]); // Handle errors by setting an empty array
        setLoading(false);
      }
    };
  
    fetchNotifications();
  }, []);  

  // Update notification status to 'read' or 'unread'
  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/notification/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      if (data.notification) {
        // Update the notification list with the updated status
        setNotifications((prevNotifications) =>
          prevNotifications.map((notif) =>
            notif.id === id ? { ...notif, status: newStatus } : notif
          )
        );
      }
    } catch (error) {
      console.error('Error updating notification status:', error);
    }
  };

  // Skeleton Loader Component
  const SkeletonLoader = () => (
    <div className="animate-pulse space-y-4">
      <div className="flex flex-col p-4 rounded-lg shadow-lg bg-white">
        <div className="flex justify-between items-center">
          <div className="h-4 bg-gray-300 rounded w-1/3"></div>
          <div className="h-3 bg-gray-300 rounded w-1/4"></div>
        </div>
        <div className="flex justify-between items-center mt-2">
          <div className="h-3 bg-gray-300 rounded w-1/4"></div>
          <div className="h-8 bg-gray-300 rounded w-1/3"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {loading ? (
        <SkeletonLoader />
      ) : notifications.length === 0 ? (
        <div className="text-center text-gray-500">No notifications yet.</div>
      ) : notifications.length === 1 ? (
        // Special UI for a single notification
        <div
          key={notifications[0].id}
          className={`flex flex-col p-4 rounded-lg shadow-lg bg-white ${notifications[0].status === 'unread' ? 'border-l-4 border-rose-500' : 'border-l-4 border-gray-300'
            }`}
        >
          <div className="flex justify-between items-center">
            <div className="text-sm font-semibold text-gray-800">{notifications[0].message}</div>
            <div className="text-xs text-gray-400">
              {new Date(notifications[0].createdAt).toLocaleDateString()}
            </div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs font-medium text-gray-600">{notifications[0].type}</div>
            <button
              className={`px-4 py-1 rounded-md text-sm font-semibold ${notifications[0].status === 'unread' ? 'bg-rose-500 text-white' : 'bg-gray-300 text-gray-700'
                }`}
              onClick={() => handleStatusChange(notifications[0].id, notifications[0].status === 'unread' ? 'read' : 'unread')}
            >
              {notifications[0].status === 'unread' ? 'Mark as Read' : 'Mark as Unread'}
            </button>
          </div>
        </div>
      ) : (
        // Normal rendering for multiple notifications
        notifications.map((notification) => (
          <div
            key={notification.id}
            className={`flex flex-col p-4 rounded-lg shadow-lg bg-white ${notification.status === 'unread' ? 'border-l-4 border-rose-500' : 'border-l-4 border-gray-300'
              }`}
          >
            <div className="flex justify-between items-center">
              <div className="text-sm font-semibold text-gray-800">{notification.message}</div>
              <div className="text-xs text-gray-400">
                {new Date(notification.createdAt).toLocaleDateString()}
              </div>
            </div>
            <div className="flex justify-between items-center mt-2">
              <div className="text-xs font-medium text-gray-600">{notification.type}</div>
              <button
                className={`px-4 py-1 rounded-md text-sm font-semibold ${notification.status === 'unread' ? 'bg-rose-500 text-white' : 'bg-gray-300 text-gray-700'
                  }`}
                onClick={() => handleStatusChange(notification.id, notification.status === 'unread' ? 'read' : 'unread')}
              >
                {notification.status === 'unread' ? 'Mark as Read' : 'Mark as Unread'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default Notifications;
